// © Microsoft Corporation. All rights reserved.

/**
 * Helper functions for all internal logic.
 */

/**
 * Timestamp Seed.
 * @var timestampSeed Seed used to random timestamp.
 */
let timestampSeed: number = new Date().getTime();

/**
 * clientId value
 * @var clientIdValue MUID value from the first read.
 */
let clientIdValue: string;

/**
 * session storage
 * @var sessionStorage session storage object.
 */
export let sessionStorage;

try {
    sessionStorage = "sessionStorage" in window && window.sessionStorage;
} catch (ex) {
    // Getting session storage failed. Can happen in Chrome when user has disabled cookies.
}

/**
 * Generate a random timestamp-seeded hex digit.
 * @param c Source.
 * @returns A random timestamp-seeded hex digit.
 */
function getDigit(c: any): any {
    const seededRandom = (timestampSeed + Math.random() * 16) % 16 | 0;

    // adjust the timestamp seed so that it changes for the first thirteen characters
    timestampSeed = Math.floor(timestampSeed / 16);

    // if the position is an "x", just use the random number. If it's a "y", we
    // we want to make sure the value is only the lowest three bits of the random
    // number, with a 1 in the 4-bit position. See RFC4122 for more info.
    return (c === "x" ? seededRandom : (seededRandom & 0x7 | 0x8)).toString(16);
}

/**
 * Generate guid.
 * @returns Guid.
 */
export function generateGuid(): string {
    // generate an additional random timestamp-base seed.
    // the Math.random function is itself seeded, but if two clients happen to have
    // seeded their random number generator with the same value, then they will generate
    // duplicate guids and collide. With this added timestamp seed, the first thirteen characters
    // will be adjusted by the timestamp of the call as well, so even if the clients' random number generated
    // are seeded with the same value, they would have to call this method at the EXACT same millisecond
    // to generate the same GUID. Probably not a very high probability.
    return "xxxxxxxxxxxx4xxxyxxxxxxxxxxxxxxx".replace(/[xy]/g, getDigit);
}

/**
 * Generate current time.
 * @returns Current datetime in number format.
 */
export function getCurrentTime(): number {
    return +(new Date());
}

/**
 * Append OCID to the url.
 * @param ocid OCID value.
 * @param url Url.
 * @returns Url with OCID.
 */
export function appendOcid(ocid: string, url: string): string {
    if (url && ocid) {
        const lowerCaseUrl = url.toLowerCase();
        if (lowerCaseUrl.indexOf("?ocid=") == -1 && lowerCaseUrl.indexOf("&ocid=") == -1) {
            const token = (url.indexOf("?") == -1) ? "?" : "&";
            url = `${url}${token}ocid=${encodeURIComponent(ocid)}`;
        }
    }

    return url;
}

/**
 * Get browser width.
 * @returns Browser width.
 */
export function getBrowserWidth(): number {
    return isBrowserEnv && (window.outerWidth || document.documentElement.clientWidth);
}

/**
 * Get browser height.
 * @returns Browser height.
 */
export function getBrowserHeight(): number {
    return isBrowserEnv && (window.outerHeight || document.documentElement.clientHeight);
}

/**
 * Get scroll offset.
 * @returns Scroll offset.
 */
export function getScrollOffset() {
    const scrollOff = (isBrowserEnv ? (window.pageYOffset || document.body.scrollTop || document.documentElement.scrollTop || 0) : 0);
    return scrollOff;
}

/**
 * Safe get.
 * @param object Source object.
 * @param getFunction Function.
 * @param defaultValue Default value if it fails.
 * @returns Evaluated value of unsafe function if there is no error. Otherwise, return default value.
 */
export function safeGet<T1, T2>(object: T1, getFunction: (x: T1) => T2, defaultValue: any = undefined): T2 {
    try {
        return getFunction(object);
    } catch (error) {
        return defaultValue;
    }
}

/**
 * Get current absolute document height.
 * @returns Doc height.
 */
export function getDocHeight(): number {
    // All of these below cause reflow which has high perf implication.
    // TODO: Optimize.
    return isBrowserEnv && Math.max(
        document.body.scrollHeight || 0,
        document.documentElement.scrollHeight || 0,
        document.body.offsetHeight || 0,
        document.documentElement.offsetHeight || 0,
        document.body.clientHeight || 0,
        document.documentElement.clientHeight || 0
    );
}

/**
 * Get MUID cookie.
 * @returns MUID value.
 */
export function getMuid(): string {
    if (!isBrowserEnv) {
        return "";
    }
    const cookieName = "MUID";
    const re = new RegExp("\\b" + cookieName + "\\s*=\\s*([^;]*)", "i");
    const match = re.exec(document.cookie);
    return (match && match.length > 1 ? match[1] : "");
}

/**
 * Get static MUID cookie from the first read (might happen before MUID sync).
 * @param fallback fallback value in case muid is not available at the time.
 * @returns clientId value
 */
export function getClientId(fallback: any): string {
    if (!clientIdValue && clientIdValue !== "") {
        clientIdValue = getMuid() || fallback || "";
    }

    return clientIdValue;
}

/**
 * Checks if the DOM is available to access and use.
 * True if DOM is available, or false if it is not. This is typically true for
 * client side rendering and false for server side rendering.
 */
export const isBrowserEnv = !!(typeof window !== "undefined" && window.document && window.document.createElement);

/**
 * Get page url with ocid.
 * @param locationHref Current locationHref
 * @param ocid OCID.
 * @returns page url with ocid.
 */
export function getPageUrl(locationHref: string, ocid: string): string {
    return appendOcid(ocid, locationHref);
}

/**
 * Add ocid on clicked article urls on the target element.
 * @param target target element.
 * @param ocid ocid value.
 */
export function addOcid(target: HTMLElement, ocid: string): void {
    addTelemetryQueryParam(target, "ocid", ocid);
}

/**
 * Add pccode on clicked article urls on the target element.
 * @param target target element.
 * @param pccode pccode value.
 */
export function addPcCode(target: HTMLElement, pccode: string): void {
    let currentValue = pccode;

    try {
        const url = new URL(location.href); // Url constructor throws if an href is invalid.
        currentValue = url.searchParams.get("pc") || url.searchParams.get("PC") || url.searchParams.get("pC") || url.searchParams.get("Pc") || currentValue;
    } catch {
        // Suppress error.
    }

    addTelemetryQueryParam(target, "pc", currentValue || pccode);
}

/**
 * Add telemetry query param on clicked article urls on the target element.
 * @param target target element.
 * @param key key.
 * @param value value.
 */
export function addTelemetryQueryParam(target: HTMLElement, key: string, value: string): void {
    if (!target || !key) {
        return;
    }

    let newTarget = target as HTMLAnchorElement;
    if (newTarget.nodeName !== "a" && newTarget.closest("a")) {
        newTarget = newTarget.closest("a");
    }

    if (!newTarget) {
        return;
    }

    const { href } = newTarget as HTMLAnchorElement;

    if (href) {
        try {
            const url = new URL(href); // Url constructor throws if an href is invalid.
            const currentValue = url.searchParams.get(key);
            if (!currentValue && value && (url.host.includes("msn.com") || url.host.includes("msn.cn"))) {
                url.searchParams.set(key, value);
                newTarget.href = url.href;
            }
        } catch {
            // Suppress the error
        }
    }
}

/**
 * Get headline from element
 * @param element target element.
 * @param headline overriding headline value.
 * @return a headline
 */
export function getHeadline(element: Element, headline = "") {
    if (!element) {
        return;
    }

    // TODO: clean up how we find a headline. Depending on the markup of the page leads to bugs when we update it.
    // find headline value if needed, one may have been passed in through a data-hl attribute.
    const dataHlValue = element.getAttribute("data-hl") || "";
    if (!headline && !dataHlValue) {
        // See if there is an h4 or h3 element, no more than 2 deep.
        const headlineElement = element.querySelector("h4") || element.querySelector("h3");
        const depth = getDepth(element, headlineElement);
        if (headlineElement && depth && depth <= 2) {
            headline = getText(headlineElement);
        }
    }

    // find headline by fetching the value of title class element from anchor element if available.
    const titleElement = element instanceof HTMLAnchorElement ? element.querySelector(".title") : "";
    if (!headline && titleElement) {
        headline = getText(titleElement as HTMLElement);
    }

    const titleText = element instanceof HTMLAnchorElement ? element.getAttribute("title") : "";
    if (!headline && titleText) {
        headline = titleText;
    }

    const ariaLable = element instanceof HTMLAnchorElement ? element.getAttribute("aria-lable") : "";
    if (!headline && ariaLable) {
        headline = ariaLable;
    }

    if (typeof headline !== "string") {
        headline = dataHlValue || element.getAttribute("data-piitxt") ||
            ("FORM" == element.nodeName ? "[form submit]" :
                element.getAttribute("title") ||
                depthFirstChildAttribute(element, "title", "img") ||
                getText(element as HTMLElement) ||
                element.getAttribute("alt") ||
                depthFirstChildAttribute(element, "alt") || "");
    }

    // Added condition to call trim function only if it is defined, it fails for browsers <=IE8
    if (headline.trim != undefined) {
        headline = headline.trim();
    }

    return headline;
}

/**
 * Get depth between parent and child
 * @param parent parent.
 * @param child child.
 * @return depth
 */
export function getDepth(parent: Element, child: Element, limitDepth = 2) {
    if (!child) {
        return;
    }

    let elem = child;
    let i = 0;
    for (; elem && elem !== parent; elem = elem.parentElement, i++) {
        if (i > limitDepth) {
            return;
        }
    }

    return i;
}

/**
 * Get text from element
 * @param elem target element.
 * @return text.
 */
const trimRegex = /(^\s+)|(\s+$)/mg;
export function getText(elem: HTMLElement) {
    return (elem && (elem.textContent || elem.innerText) || "").replace(trimRegex, "");
}

/**
 * Find the first attribute from the element in the depthFirst manner skips the element if it is specified in elementName
 * @param elem target element.
 * @param attributeName attribute name.
 * @param skipElement element that would like to skip.
 * @return attribute.
 */
function depthFirstChildAttribute(element: Element, attributeName: string, skipElement = "") {
    const children = element.children || [];
    let ndx = 0;
    let altValue;

    attributeName = attributeName || "alt";

    for (; ndx < children.length; ndx++) {
        altValue = children[ndx].getAttribute(attributeName)
            || depthFirstChildAttribute(children[ndx], attributeName, skipElement);

        if (altValue && !(skipElement == children[ndx].localName)) {
            return altValue;
        }
    }
}

/**
 * Find closest element based on selector.
 * @param elem target element.
 * @param selector css selector.
 * @return closest element.
 */
export function closestElement(element, selector): Element {
    if (!element || element === document || element === window) {
        return null;
    }

    // found the closest parent with the selector (data-t)
    const found = element.closest(selector);

    // if not found, meaning that between element and data-t parent has shadow root somewhere.
    // get shadow root and find closest again
    if (found) {
        return found;
    }

    const root = getRootNode(element);
    if (root && (root as any).host) {
        return closestElement((root as any).host, selector);
    }
}

// https://github.com/webcomponents/polyfills/blob/d152fd4b436f51c708845b04b9826123ce6d93f3/packages/formdata-event/ts_src/environment_api/node.ts
/**
 * Get root node.
 * @param element target element.
 * @return root node of target element.
 */
export function getRootNode(element: Node): Node {
    if (element.parentNode) {
        return getRootNode(element.parentNode);
    }

    return element;
}

/**
 * Function to get all telemetry elements including the one under shadow roots/webcomponent.
 * @param elems element array.
 * @return telemetry elements in array.
 */
export function getAllTelemetryElements(root: Element): Element[] {
    const telemetryElements: Element[] = [];
    if (!root) {
        return;
    }

    let elems: NodeListOf<Element>;
    if (isShadowRoot(root)) {
        elems = root.shadowRoot.querySelectorAll("*");
    } else {
        elems = root.querySelectorAll("*");
    }

    getAllTelemetryElementsInternal(Array.from(elems), telemetryElements);
    return telemetryElements;
}

/**
 * Internal function to get all telemetry elements including the one under shadow roots/webcomponent.
 * @param elems element array.
 * @param telemetryElements output telemetry elements array.
 */
function getAllTelemetryElementsInternal(elems: Element[], telemetryElements = []) {
    for (let i = 0; i < elems.length; i++) {
        const elem = elems[i];
        if (isTelemetryElement(elem)) {
            // If it annotated with telemetry tag, add to output array.
            telemetryElements.push(elem);
        }
        if (isShadowRoot(elem)) {
            // since shadow root is considered self encapsulation and events do not get bubbled up,
            // need to process all elements under shadow root
            getAllTelemetryElementsInternal(Array.from(elem.shadowRoot.querySelectorAll("*")), telemetryElements);
        }
    }
}

/**
 * Check whether element is shadow root or not.
 * @param elem element.
 * @returns true if it is shadow root.
 */
export function isShadowRoot(elem: Element): boolean {
    return !!elem.shadowRoot;
}

/**
 * Check whether element contains telemetry attribute.
 * @param elem element.
 * @returns true if it is telemetry element.
 */
export function isTelemetryElement(elem: Element): boolean {
    return elem.hasAttribute && elem.hasAttribute("data-t");
}

/**
 * Check whether string is valid url.
 * @param urlString url string.
 * @returns true if it is valid url.
 */
export function isValidUrl(urlString: string) {
    try {
        new URL(urlString);
    } catch (_) {
        return false;
    }

    return true;
}

/**
* Helper for dispatching event.
* @param eventName event name.
* @param elem element.
*/
export function dispatchTelemetryEvent(eventName: string, elem: Element) {
    // Create the event.
    const event = new CustomEvent(eventName, {
        detail: {
            element: elem
        }
    });

    dispatchEvent(event);
}